import { GlobalStyles as MUIGlobalStyles } from '@mui/material';

export const GlobalStyles = () => (
  <MUIGlobalStyles
    styles={`
      /* Reset margins/padding */
      * {
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
      }
      
      html {
        /* Fix iOS 100vh bug */
        height: -webkit-fill-available;
        overflow-x: hidden;
        margin: 0;
        padding: 0;
        /* Prevent text size adjustment on orientation change */
        -webkit-text-size-adjust: 100%;
        -moz-text-size-adjust: 100%;
        -ms-text-size-adjust: 100%;
        text-size-adjust: 100%;
      }
      
      body, #root {
        min-height: 100vh !important;
        /* Fix iOS 100vh bug */
        min-height: -webkit-fill-available !important;
        width: 100%;
        max-width: 100%;
        overflow-x: hidden !important;
        margin: 0;
        padding: 0;
        /* Prevent pull-to-refresh on entire page */
        overscroll-behavior-y: contain;
        /* Smooth scrolling */
        -webkit-overflow-scrolling: touch;
        scroll-behavior: smooth;
      }

      /* Cross-platform PWA Safe Area Support */
      @supports (padding: max(0px)) {
        html, body {
          padding-left: env(safe-area-inset-left);
          padding-right: env(safe-area-inset-right);
        }
        
        #root {
          padding-top: env(safe-area-inset-top);
          padding-bottom: env(safe-area-inset-bottom);
        }
      }

      /* iOS PWA Status Bar Handling */
      @supports (padding: max(0px)) {
        @media (display-mode: standalone) {
          body {
            /* Status bar overlay - translucent */
            padding-top: env(safe-area-inset-top);
            /* Prevent overscroll bounce effect */
            overscroll-behavior-y: none;
          }
          
          #root {
            min-height: calc(100vh - env(safe-area-inset-top));
            min-height: -webkit-fill-available;
          }

          /* Create a dynamic pseudo-element for status bar area */
          body::before {
            content: '';
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            height: env(safe-area-inset-top);
            background-color: rgba(255, 255, 255, 0.85);
            backdrop-filter: saturate(180%) blur(20px);
            -webkit-backdrop-filter: saturate(180%) blur(20px);
            z-index: 1250;
            pointer-events: none;
            transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          /* Solid status bar when header is solid */
          body.header-solid::before {
            background-color: #ffffff;
            backdrop-filter: none;
            -webkit-backdrop-filter: none;
          }
        }
      }

      /* Android PWA specific adjustments */
      @supports (padding: max(0px)) {
        @media (display-mode: standalone) {
          /* Android navigation bar handling */
          body {
            padding-bottom: env(safe-area-inset-bottom);
          }
        }
        
        /* Android Material Design theme */
        @media (display-mode: standalone) and (max-width: 768px) {
          body::before {
            /* Android status bar - solid color */
            background-color: #113826;
            backdrop-filter: none;
            -webkit-backdrop-filter: none;
          }
          
          body.header-solid::before {
            background-color: #ffffff;
          }
        }
      }

      /* Touch Action and Scroll Improvements */
      body, #root {
        /* Prevent horizontal scrolling/panning */
        touch-action: pan-y pinch-zoom;
      }

      /* Allow only vertical scrolling on main content areas */
      main, .main-content, .content-container, [role="main"] {
        touch-action: pan-y pinch-zoom;
        -webkit-overflow-scrolling: touch;
      }

      /* Prevent rubber banding on iOS - controlled areas */
      .no-overscroll {
        overscroll-behavior: none;
        -webkit-overflow-scrolling: touch;
      }

      /* iOS Keyboard Handling */
      @supports (-webkit-touch-callout: none) {
        /* Fix viewport height when keyboard appears */
        body.keyboard-visible {
          height: 100vh;
          overflow: hidden;
        }

        /* Prevent background scroll when input is focused */
        body.input-focused {
          position: fixed;
          width: 100%;
          overflow: hidden;
        }

        /* Ensure smooth scroll restoration after keyboard dismissal */
        input, textarea, select {
          /* Prevent iOS zoom on focus */
          font-size: 16px !important;
        }

        /* Visual Viewport API handling for keyboard */
        @media (display-mode: standalone) {
          .keyboard-spacer {
            /* Dynamic spacer for keyboard */
            height: 0;
            transition: height 0.3s ease-out;
          }
        }
      }

      /* Android Keyboard Handling */
      @media (display-mode: standalone) {
        /* Adjust layout when keyboard is visible */
        body.keyboard-visible {
          /* Android resizes viewport, so we maintain scroll */
          overflow-y: auto;
        }

        /* Prevent horizontal scroll when keyboard opens */
        input:focus, textarea:focus {
          scroll-margin-top: 20px;
          scroll-margin-bottom: 20px;
        }
      }

      /* Smooth Scroll Behavior */
      @media (prefers-reduced-motion: no-preference) {
        html {
          scroll-behavior: smooth;
        }
        
        /* Smooth scroll for content areas */
        .smooth-scroll {
          scroll-behavior: smooth;
        }
      }

      /* Disable smooth scroll for users who prefer reduced motion */
      @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
          scroll-behavior: auto !important;
        }
      }

      /* Pull to Refresh Styling - Enhanced for Android */
      .pull-to-refresh-container {
        position: relative;
        overflow: hidden;
        /* Prevent default Android pull-to-refresh */
        overscroll-behavior-y: contain;
      }

      .pull-to-refresh-indicator {
        position: absolute;
        top: -60px;
        left: 50%;
        transform: translateX(-50%);
        width: 40px;
        height: 40px;
        background: rgba(17, 56, 38, 0.9);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        z-index: 9999;
        transition: all 0.3s ease;
        box-shadow: 0 2px 10px rgba(0,0,0,0.2);
        /* Better visibility on Android */
        backdrop-filter: blur(8px);
      }

      .pull-to-refresh-indicator.visible {
        top: max(env(safe-area-inset-top), 10px);
      }

      .pull-to-refresh-indicator.loading {
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        from { transform: translateX(-50%) rotate(0deg); }
        to { transform: translateX(-50%) rotate(360deg); }
      }

      /* Android Material Design pull indicator */
      @media (display-mode: standalone) and (max-width: 768px) {
        .pull-to-refresh-indicator {
          background: linear-gradient(135deg, rgba(17, 56, 38, 0.95) 0%, rgba(17, 56, 38, 0.85) 100%);
          box-shadow: 
            0 2px 10px rgba(0,0,0,0.15),
            0 1px 3px rgba(0,0,0,0.1);
        }
      }

      /* PWA Sidebar Adjustments */
      .sidebar-container {
        /* Default browser behavior */
        padding-top: 0;
      }

      /* iOS PWA Sidebar adjustments */
      @media (display-mode: standalone) {
        .sidebar-container {
          padding-top: max(env(safe-area-inset-top), 20px);
          padding-left: env(safe-area-inset-left);
          padding-right: env(safe-area-inset-right);
        }
        
        .sidebar-content {
          /* Add extra spacing at top for better visual balance */
          padding-top: 10px;
        }

        /* Create a pseudo-element to fill the safe area above the sidebar */
        .sidebar-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: env(safe-area-inset-top);
          background-color: #F4F4F4; /* Match sidebar background */
          z-index: 1;
          pointer-events: none;
        }
      }

      /* Android PWA Sidebar adjustments */
      @media (display-mode: standalone) and (orientation: portrait) {
        .sidebar-container {
          /* Android typically needs less padding */
          padding-top: max(env(safe-area-inset-top), 10px);
        }

        /* Android sidebar safe area fill */
        .sidebar-container::before {
          background-color: #F4F4F4; /* Match sidebar background */
          height: max(env(safe-area-inset-top), 10px);
        }
      }

      /* Mobile sidebar overlay adjustments */
      @media (display-mode: standalone) and (max-width: 768px) {
        .sidebar-drawer {
          /* Ensure sidebar drawer respects safe areas on mobile */
          top: max(env(safe-area-inset-top), 0px);
          height: calc(100vh - max(env(safe-area-inset-top), 0px) - max(env(safe-area-inset-bottom), 0px));
        }
      }

      /* Ensure text on primary-colored backgrounds is always white */
      .MuiButton-containedPrimary {
        color: #ffffff !important;
      }

      /* Navigation link styling */
      .nav-link {
        border-bottom: 2px solid transparent;
        font-weight: 600 !important;
      }
      .nav-link.text-dark {
        color: #252525 !important;
      }
      .nav-link.text-light {
        color: #fefefe !important;
      }
      .nav-link.active {
        color: #e4ac3a !important;
        border-color: #e4ac3a !important;
        font-weight: 700 !important;
      }
      .nav-link:hover {
        color: #cd9b33 !important;
      }

      /* Replacing the old .landing-linear-bg usage */
      .landing-linear-bg {
        /* If you originally had background-attachment: fixed, remove or override it for mobile: */
        background-attachment: fixed; 
      }

      /* On smaller screens, remove 'fixed' to avoid jerkiness: */
      @media (max-width: 768px) {
        .landing-linear-bg {
          background-attachment: scroll !important;
        }
      }

      /* Global table and scrollable container styles */
      .MuiBox-root, .MuiContainer-root, .MuiPaper-root {
        max-width: 100%;
      }

      /* Ensure all DataGrid components have proper overflow handling */
      .MuiDataGrid-root {
        width: 100% !important;
        overflow: visible !important;
        max-width: 100% !important;
      }

      /* Prevent overflow in data grid elements */
      .MuiDataGrid-main,
      .MuiDataGrid-columnHeaders,
      .MuiDataGrid-virtualScroller,
      .MuiDataGrid-virtualScrollerContent,
      .MuiDataGrid-virtualScrollerRenderZone,
      .MuiDataGrid-footerContainer {
        max-width: 100% !important;
        min-width: 0 !important;
      }

      /* Enable text wrapping in grid cells for small screens */
      @media (max-width: 600px) {
        .MuiDataGrid-cell, .MuiDataGrid-cell span, .MuiDataGrid-cell div {
          white-space: normal !important;
          word-break: break-word !important;
          overflow-wrap: break-word !important;
          line-height: 1.5 !important;
        }
      }

      /* Proper scrolling for small screens */
      .table-container {
        width: 100%;
        max-width: 100%;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
      }

      .zoom-in {
        transition: all .2s ease;
      }
      .zoom-in:hover {
        scale: 1.05;
      }

      .border-shadow {
        box-shadow: rgba(0, 0, 0, 0.04) 0px 5px 22px 0px,
                    rgba(0, 0, 0, 0.06) 0px 0px 0px 1px;
      }

      .footer-bg {
        background-color: #1c2536 !important;
      }
      .footer-bg > * {
        color: #fefefe;
      }
      .footer-bg > a {
        color: #fefefe;
      }

      /* Carousel specific styles */
      .carousel-card {
        filter: drop-shadow(0 0 25px rgba(0, 0, 0, 0.2));
        transition: all 0.4s ease-in-out;
        transform: scale(0.95);
      }
      
      .carousel-card:hover {
        transform: scale(1);
        filter: drop-shadow(0 0 30px rgba(0, 0, 0, 0.25));
      }

      .carousel-image {
        border-radius: 16px;
        object-fit: cover;
      }

      /* Benefits section styles */
      .benefits-container {
        width: 100%;
        overflow: hidden;
      }

      .benefits-content {
        max-width: 1200px;
        margin: 0 auto;
      }

      /* PWA Standalone Mode Enhancements */
      body.pwa-standalone {
        /* Prevent pull-to-refresh on entire page in standalone */
        overscroll-behavior-y: none;
      }

      /* Smooth header transitions in standalone mode */
      body.pwa-standalone .MuiToolbar-root {
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }

      /* Prevent layout shift when header hides */
      @media (display-mode: standalone) {
        main, [role="main"], .main-content {
          /* Account for header height */
          padding-top: 64px;
          transition: padding-top 0.3s ease;
        }

        @media (min-width: 768px) {
          main, [role="main"], .main-content {
            padding-top: 72px;
          }
        }
      }

      /* Better z-index layering for PWA */
      .pwa-status-bar {
        z-index: 1250;
      }

      .pwa-header {
        z-index: 1200;
      }

      .pwa-sidebar {
        z-index: 1100;
      }

      .pwa-modal {
        z-index: 1300;
      }

      /* ===== CATEGORY 11: MOBILE SPACING & LAYOUT OPTIMIZATIONS ===== */
      
      /* Prevent horizontal scroll from wide content */
      *, *::before, *::after {
        box-sizing: border-box;
        max-width: 100%;
      }

      /* Prevent images and media from causing horizontal scroll */
      img, video, iframe, embed, object {
        max-width: 100%;
        height: auto;
      }

      /* Handle long text/URLs without breaking layout */
      p, h1, h2, h3, h4, h5, h6, span, div, a {
        word-wrap: break-word;
        overflow-wrap: break-word;
        hyphens: auto;
      }

      /* Prevent long URLs from causing horizontal scroll */
      a {
        word-break: break-all;
      }

      /* Code blocks and pre-formatted text */
      pre, code {
        max-width: 100%;
        overflow-x: auto;
        word-wrap: break-word;
        white-space: pre-wrap;
      }

      /* Tables should scroll horizontally on mobile */
      @media (max-width: 768px) {
        table {
          display: block;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          max-width: 100%;
        }
      }

      /* Container responsive padding (Category 11 Task 11.1) */
      @media (max-width: 600px) {
        .MuiContainer-root {
          padding-left: 16px !important;
          padding-right: 16px !important;
        }
      }

      /* Better text truncation pattern */
      .text-truncate {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .text-truncate-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .text-truncate-3 {
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* Improved focus indicators for accessibility */
      *:focus-visible {
        outline: 3px solid rgba(17, 56, 38, 0.5);
        outline-offset: 2px;
        border-radius: 4px;
      }

      /* Enhanced button focus for touch devices */
      button:focus-visible, a:focus-visible {
        outline: 3px solid rgba(17, 56, 38, 0.5);
        outline-offset: 2px;
      }

      /* Better scrollbar styling for mobile */
      @media (max-width: 768px) {
        ::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }

        ::-webkit-scrollbar-track {
          background: transparent;
        }

        ::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.15);
          border-radius: 2px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.25);
        }
      }

      /* Optimize spacing for mobile screens */
      @media (max-width: 600px) {
        /* Reduce excessive vertical spacing */
        .MuiGrid-root.MuiGrid-container {
          margin-top: -16px !important;
        }

        .MuiGrid-root.MuiGrid-item {
          padding-top: 16px !important;
        }
      }

      /* Better handling of long content in cards */
      .MuiCard-root {
        overflow: hidden;
        word-wrap: break-word;
      }

      .MuiCardContent-root {
        overflow-wrap: break-word;
      }

      /* Prevent layout shift from dynamic content */
      .content-container {
        contain: layout style paint;
      }

      /* Better mobile spacing for Material-UI components */
      @media (max-width: 600px) {
        /* Reduce Dialog padding on mobile */
        .MuiDialog-paper {
          margin: 16px !important;
        }

        /* Reduce Card padding on mobile */
        .MuiCard-root .MuiCardContent-root {
          padding: 16px !important;
        }

        /* Stack spacing optimization */
        .MuiStack-root {
          gap: 16px !important;
        }
      }

      /* ============================================
       * ACCESSIBILITY - TOUCH TARGET UTILITIES
       * ============================================
       * WCAG 2.1 AAA Standard: Minimum 44x44px touch targets
       * Material Design: Minimum 48x48px recommended
       */

      /* Utility classes for touch target enforcement */
      .touch-target-min {
        min-width: 44px !important;
        min-height: 44px !important;
      }

      .touch-target-optimal {
        min-width: 48px !important;
        min-height: 48px !important;
      }

      .touch-target-large {
        min-width: 56px !important;
        min-height: 56px !important;
      }

      /* Clickable elements - ensure adequate spacing */
      .clickable-spacing {
        margin: 4px !important;
      }

      /* Icon-only buttons - enforce minimum size */
      .icon-button-accessible {
        min-width: 44px !important;
        min-height: 44px !important;
        padding: 10px !important;
      }

      /* Link buttons - ensure they're easily tappable */
      a.button-link,
      button.link-style {
        min-height: 44px !important;
        padding: 10px 16px !important;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      /* Mobile-specific touch target optimizations */
      @media (max-width: 600px) {
        .touch-target-mobile {
          min-width: 48px !important;
          min-height: 48px !important;
          padding: 12px !important;
        }

        /* Increase spacing between interactive elements on mobile */
        .MuiButtonGroup-root .MuiButton-root,
        .MuiToggleButtonGroup-root .MuiToggleButton-root {
          min-height: 48px !important;
        }

        /* Ensure adequate tap target for close buttons in dialogs */
        .MuiDialog-root .MuiIconButton-root,
        .MuiDrawer-root .MuiIconButton-root {
          min-width: 48px !important;
          min-height: 48px !important;
        }
      }
    `}
  />
);
